import { useEffect, useState } from 'react';
import { useMMECTheme } from '../contexts/MMECThemeContext';

/**
 * Palette → canvas-chart colours.
 *
 * SVG charts (Recharts) are themed by mmec-canvas.css. Canvas charts
 * (lightweight-charts) paint from option objects, so they read the active
 * palette here at creation and again whenever the palette pill changes.
 * Every value has a fallback so a chart still renders before the tokens load.
 */
export interface ChartTheme {
  background: string;
  text: string;
  grid: string;
  border: string;
  up: string;
  down: string;
  accent: string;
  muted: string;
}

const FALLBACK: ChartTheme = {
  background: '#0a0a0f',
  text: '#9ca3af',
  grid: '#1f2937',
  border: '#374151',
  up: '#26a69a',
  down: '#ef5350',
  accent: '#38bdf8',
  muted: '#64748b',
};

function token(cs: CSSStyleDeclaration, name: string, fallback: string): string {
  const v = cs.getPropertyValue(name).trim();
  return v || fallback;
}

/** Hairlines: the palette's --hair is an rgba() which lightweight-charts accepts as-is. */
export function readChartTheme(): ChartTheme {
  if (typeof window === 'undefined') return FALLBACK;
  try {
    const cs = getComputedStyle(document.documentElement);
    return {
      background: token(cs, '--void', FALLBACK.background),
      text: token(cs, '--muted', FALLBACK.text),
      grid: token(cs, '--hair', FALLBACK.grid),
      border: token(cs, '--border', FALLBACK.border),
      up: token(cs, '--up', FALLBACK.up),
      down: token(cs, '--down', FALLBACK.down),
      accent: token(cs, '--acc', FALLBACK.accent),
      muted: token(cs, '--muted-2', FALLBACK.muted),
    };
  } catch {
    return FALLBACK;
  }
}

/** The lightweight-charts option slice that carries palette colour. */
export function lwChartColours(t: ChartTheme) {
  return {
    layout: {
      background: { color: t.background },
      textColor: t.text,
      fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
      fontSize: 11,
    },
    grid: {
      vertLines: { color: t.grid },
      horzLines: { color: t.grid },
    },
    rightPriceScale: { borderColor: t.border },
    leftPriceScale: { borderColor: t.border },
    timeScale: { borderColor: t.border },
    crosshair: {
      vertLine: { color: t.muted, labelBackgroundColor: t.border },
      horzLine: { color: t.muted, labelBackgroundColor: t.border },
    },
  };
}

/**
 * Current chart theme, re-read when the palette changes. Tokens are applied to
 * <html> synchronously by MMECThemeContext, so reading on the next tick sees
 * the new values.
 */
export function useChartTheme(): ChartTheme {
  const { palette } = useMMECTheme();
  const [theme, setTheme] = useState<ChartTheme>(readChartTheme);
  useEffect(() => {
    const id = window.setTimeout(() => setTheme(readChartTheme()), 0);
    return () => window.clearTimeout(id);
  }, [palette]);
  return theme;
}
